import bnDictionary from './bnDictionary';

// Runtime English -> Bangla translation of rendered UI text, so every page can be
// shown in Bangla without rewriting each component. Original text is remembered
// so switching back to English restores it exactly.

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const toBn = (s) => s.replace(/[0-9]/g, (d) => BN_DIGITS[+d]);

const dict = new Map();
Object.keys(bnDictionary).forEach((k) => {
  if (k !== k.trim()) return; // ignore accidental trailing-space duplicates
  dict.set(k, bnDictionary[k]);
  dict.set(k.toLowerCase(), bnDictionary[k]);
});

const PATTERNS = [
  [/^Class (\d+)$/i, (m) => `শ্রেণি ${toBn(m[1])}`],
  [/^Class (\d+) \((\d+)\)$/i, (m) => `শ্রেণি ${toBn(m[1])} (${toBn(m[2])})`],
  [/^Sec (.+)$/i, (m) => `শাখা ${translate(m[1])}`],
  [/^(\d+) students?$/i, (m) => `${toBn(m[1])} জন শিক্ষার্থী`],
  [/^(\d+) sections?$/i, (m) => `${toBn(m[1])} টি শাখা`],
  [/^(\d+)% fees paid$/i, (m) => `${toBn(m[1])}% ফি পরিশোধিত`],
  [/^(\d+) days?$/i, (m) => `${toBn(m[1])} দিন`],
  [/^(\d+) (mins?|minutes?) ago$/i, (m) => `${toBn(m[1])} মিনিট আগে`],
  [/^(\d+) hours? ago$/i, (m) => `${toBn(m[1])} ঘণ্টা আগে`],
  [/^(\d+) transactions?$/i, (m) => `${toBn(m[1])} টি লেনদেন`],
  [/^(\d+) modules enabled$/i, (m) => `${toBn(m[1])} টি মডিউল চালু`],
  [/^Showing (\d+) of (\d+)/i, (m) => `${toBn(m[2])} এর মধ্যে ${toBn(m[1])} দেখানো হচ্ছে`],
  [/^Students \((\d+)\)$/i, (m) => `শিক্ষার্থী (${toBn(m[1])})`],
  [/^Mark All Read \((\d+)\)$/i, (m) => `সব পঠিত করুন (${toBn(m[1])})`],
  [/^Step (\d+) of (\d+)$/i, (m) => `ধাপ ${toBn(m[1])} / ${toBn(m[2])}`],
];

function translate(raw) {
  const text = raw.replace(/\s+/g, ' ').trim();
  if (!text) return null;
  let out = dict.get(text) || dict.get(text.toLowerCase());
  if (!out && text.endsWith(':')) {
    const base = text.slice(0, -1).trim();
    const t = dict.get(base) || dict.get(base.toLowerCase());
    if (t) out = t + ':';
  }
  if (!out) {
    for (const [re, fn] of PATTERNS) {
      const m = text.match(re);
      if (m) { out = fn(m); break; }
    }
  }
  return out || null;
}

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'NOSCRIPT']);
const originals = new WeakMap(); // text node -> original nodeValue; element -> {attr: original}
let active = false;
let observer = null;
let scheduled = false;

function convertTextNode(node) {
  const parent = node.parentElement;
  if (!parent || SKIP_TAGS.has(parent.tagName) || parent.closest('[data-no-translate]')) return;
  const cur = node.nodeValue;
  if (!/\S/.test(cur)) return;
  let src = originals.get(node);
  // If React replaced the text since we translated, treat the new value as the new original.
  if (src === undefined || (node.__bnOut !== cur)) src = cur;
  const tr = translate(src);
  let out;
  if (tr) {
    const lead = src.match(/^\s*/)[0];
    const trail = src.match(/\s*$/)[0];
    out = lead + toBn(tr) + trail;
  } else if (/[0-9]/.test(src) && !/[A-Za-z@:/]/.test(src)) {
    out = toBn(src); // pure number-ish text (counts, amounts) -> Bangla digits
  } else {
    originals.delete(node); node.__bnOut = undefined;
    return;
  }
  originals.set(node, src);
  node.__bnOut = out;
  if (cur !== out) node.nodeValue = out;
}

const ATTRS = ['placeholder', 'title', 'aria-label'];
function convertAttrs(el) {
  for (const a of ATTRS) {
    if (!el.hasAttribute || !el.hasAttribute(a)) continue;
    const key = `__bn_${a}`;
    const cur = el.getAttribute(a);
    const src = el[key] && el[key].out === cur ? el[key].src : cur;
    const tr = translate(src);
    if (tr) {
      el[key] = { src, out: tr };
      if (cur !== tr) el.setAttribute(a, tr);
    }
  }
}

function walk(root) {
  if (!root) return;
  if (root.nodeType === 3) { convertTextNode(root); return; }
  if (root.nodeType !== 1) return;
  if (root.tagName === 'SCRIPT' || root.tagName === 'STYLE') return;
  convertAttrs(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n;
  while ((n = w.nextNode())) {
    if (n.nodeType === 3) convertTextNode(n);
    else if (n.tagName !== 'SCRIPT' && n.tagName !== 'STYLE') convertAttrs(n);
  }
}

function restore(root) {
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n;
  while ((n = w.nextNode())) {
    if (n.nodeType === 3) {
      const src = originals.get(n);
      if (src !== undefined && n.nodeValue === n.__bnOut) n.nodeValue = src;
      originals.delete(n); n.__bnOut = undefined;
    } else {
      for (const a of ATTRS) {
        const info = n[`__bn_${a}`];
        if (info && n.getAttribute(a) === info.out) n.setAttribute(a, info.src);
        n[`__bn_${a}`] = undefined;
      }
    }
  }
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    if (active) walk(document.body);
  }, 30);
}

export function enableBanglaUI() {
  if (active) return;
  active = true;
  document.documentElement.lang = 'bn';
  document.documentElement.classList.add('lang-bn');
  walk(document.body);
  observer = new MutationObserver(schedule);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export function disableBanglaUI() {
  if (!active) return;
  active = false;
  if (observer) { observer.disconnect(); observer = null; }
  restore(document.body);
  document.documentElement.lang = 'en';
  document.documentElement.classList.remove('lang-bn');
}

export function syncBanglaUI(lang) {
  if (lang && lang.toLowerCase().startsWith('bn')) enableBanglaUI();
  else disableBanglaUI();
}

// Translate a standalone HTML document (print windows) when Bangla is active.
export function localizeHtml(html) {
  if (!active) return html;
  const withFont = html.replace('<style>', "<style>body{font-family:'Noto Sans Bengali','Nirmala UI','Kalpurush','Shonar Bangla',Arial,sans-serif !important;}");
  return withFont.replace(/>([^<>]+)</g, (m, text) => {
    if (text.includes('{') || text.includes('}')) return m; // leave CSS alone
    const tr = translate(text);
    if (tr) return '>' + toBn(tr) + '<';
    if (/[0-9]/.test(text) && !/[A-Za-z@:/]/.test(text)) return '>' + toBn(text) + '<';
    return m;
  });
}
